import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getProductsById } from '@yme/api';
import { AlertType } from '@yme/atoms/alert';
import { Gallery, GalleryContext } from '@yme/atoms/gallery';
import { Col, Container, Row } from '@yme/atoms/grid';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { ACCESSORIES_ATTRIBUTE, PRODUCTS_ATTRIBUTE, ProductType, QueryParams, REQUIRED_ITEMS_ATTRIBUTE, } from '@yme/common/constants';
import { COOKIE_CART_ID_EXPIRE, COOKIE_CART_ID_KEY } from '@yme/common/redux/cart/constants';
import { getCart } from '@yme/common/redux/cart/services';
import { getCommonLabels, getLanguageRootPath, PART_CODE } from '@yme/common/selectors';
import { IndentWrapper } from '@yme/components/Separator';
import { useLocation, useWindowSize } from '@yme/hooks/index';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS, ModalState } from '@yme/modals/constants';
import { CheckCompatibility } from '@yme/pdp-components/ProductCard/CardAccessories/CheckCompatibility';
import { CardPanel } from '@yme/pdp-components/ProductCard/components/CardPanel';
import { CartModalWindow } from '@yme/pdp-components/ProductCard/components/CartModalWindow';
import { Description } from '@yme/pdp-components/ProductCard/components/Description';
import { ProductBenefits } from '@yme/pdp-components/ProductCard/components/ProductBenefits';
import { ACCESSORIES_GALLERY_CONFIG, PRODUCT_CARD_SECTION_ID, } from '@yme/pdp-components/ProductCard/constants';
import { ProductCardWrapperStyled, ProductDetailsPageWrapperStyled, } from '@yme/pdp-components/ProductCard/styled';
import { useProductCardData } from '@yme/pdp-components/ProductCard/useProductCardData';
import { getBrandSlug, getCategoryTitle, getPageLevelNavigationData, getTitle, } from '@yme/selectors/common';
import { getAttribute, getProductDetails } from '@yme/selectors/products';
import { useTranslation } from '@yme/services/i18n';
import { colorAccentError900, colorAccentSuccess800 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel4 } from '@yme/tokens/typography';
import { checkAccessoryHideProductCompatibility, deleteQueryParamByName, getAttributeByName, getQueryParamByName, } from '@yme/utils/common';
import { AspectRatio, CartBroadcastEvents } from '@yme/utils/constants';
import { getProductYear } from '@yme/utils/product';
import { broadcastCart } from '../../../../../App';
import { CheckPartAlert } from '../../CheckPartAlert';
import { selectProductCardHeroImage } from '../../HeroSection/selectors';
import { CardSkeleton } from '../components/CardSkeleton';
import { composePageTitle } from '../helpers';
import { getSelectedCompatibilityProduct, setSelectedCompatibilityProduct } from '../redux';
import { getProductCurrentSku } from '../redux/selectors';
import { MountingInformationComponent } from './MountingInformationComponent';
import { COOKIE_HYPERDRIVE_SELECTED_UNIT } from './constants';
import { getDownloadSectionItems, getKeysQueryParamString, getMarginTop, getSuitableRequiredItems, openSelectModelModal, setSelectedUnit, } from './helpers';
export function CardAccessories({ labels, paymentOptions, smartfitKey, hyperdriveEndpoints, warrantyInfo, sizeGuideAssetPath, isRemoveBottomIndent, selectModelModalModel, privacyConsentsModel, fasletModel, plpUrl = '', isInEditor, campaignDisclaimer, }) {
    const { onHideModalCart, galleryContext, galleryComponentProps, cardPanelComponentProps, productBenefitsProps, currentColorTheme, cartModalLabels, isRightToLeft, cartErrorData, isAddingProduct, isLoadingExternalCart, isOpenCartModal, isOpenExternalCartModal, isEmptyProductBenefits, structuredData, metaDescription, metaTitle, isLoadedProductDetails, } = useProductCardData({
        labels,
        paymentOptions,
        smartfitKey,
        hyperdriveEndpoints,
        galleryConfig: ACCESSORIES_GALLERY_CONFIG,
        is360ImageViewed: false,
    });
    const [requiredProducts, setRequiredProducts] = useState([]);
    const [addedRequiredProducts, setAddedRequiredProducts] = useState([]);
    const { t } = useTranslation();
    const { isInfiniteDesktopView } = useWindowSize();
    const languageRootPath = useSelector(getLanguageRootPath);
    const { data } = useSelector(getProductDetails);
    const currentSku = useSelector(getProductCurrentSku);
    const currentVariant = useMemo(() => data?.variants?.find((variant) => variant?.sku === currentSku), [data, currentSku]);
    const plpData = useSelector(getPageLevelNavigationData);
    const isPlpDataExisted = Boolean(plpData);
    const { mobileMargin, tabletMargin, desktopMargin } = useMemo(() => getMarginTop(isPlpDataExisted), [isPlpDataExisted]);
    const isAccessoryHideProductCompatibility = checkAccessoryHideProductCompatibility(data);
    const suitableProductsKeys = useSelector(getAttribute(PRODUCTS_ATTRIBUTE));
    const requiredItemKeys = useSelector(getAttribute(REQUIRED_ITEMS_ATTRIBUTE));
    const partCode = useSelector(getAttribute(PART_CODE));
    const commonLabels = useSelector(getCommonLabels);
    const isCompatibilityCheckAllowed = data?.type === ProductType.ACCESSORY &&
        selectModelModalModel.showCompatibilityCheck &&
        !isAccessoryHideProductCompatibility;
    const selectedCompatibilityProduct = useSelector(getSelectedCompatibilityProduct);
    const { hash: skuFromLocation } = useLocation();
    const cookieCompatibilityProduct = Cookies.get(COOKIE_HYPERDRIVE_SELECTED_UNIT);
    const dispatch = useDispatch();
    const history = useHistory();
    const descriptionProps = {
        label: skuFromLocation || partCode,
        title: data?.name,
        description: data?.description,
        withSubtitle: true,
        isTitleLevelOne: true,
    };
    const downloadSectionItems = useMemo(() => {
        return getDownloadSectionItems({
            variant: currentVariant,
            name: t('productCard_safety_data_sheet_label'),
        });
    }, [currentVariant, t]);
    const handleUpdateCartModal = useCallback(() => {
        dispatch(setIsModalShowed({
            type: MODALS.ALERT_MESSAGE_MODAL,
            data: {
                state: ModalState.SUCCESS,
                title: t('shoppingCart_abandonedCartNotification_header'),
                content: t('shoppingCart_abandonedCartNotification_disclaimer'),
                primaryButtonContent: t('shoppingCart_abandonedCartNotification_button'),
            },
        }));
    }, [dispatch, t]);
    useEffect(() => {
        if (!languageRootPath)
            return;
        const cartIdFromUrl = getQueryParamByName(history.location.search, QueryParams.CART_ID);
        if (cartIdFromUrl) {
            Cookies.set(COOKIE_CART_ID_KEY, cartIdFromUrl, {
                expires: COOKIE_CART_ID_EXPIRE,
                path: `${languageRootPath}/`,
            });
            deleteQueryParamByName(QueryParams.CART_ID, history);
            broadcastCart.postMessage(CartBroadcastEvents.CART_FROM_URL);
            dispatch(getCart('', true, handleUpdateCartModal));
        }
    }, [languageRootPath, handleUpdateCartModal, dispatch, history]);
    useEffect(() => () => {
        dispatch(setSelectedCompatibilityProduct(null));
    }, [dispatch]);
    const { partsAccessoriesMerch_compatibleWithYourModelAlert_description: compatibleWithYourModelAlertDescription, partsAccessoriesMerch_isNotCompatibleWithYourModelAlert_description: isNotCompatibleWithYourModelAlert, partsAccessoriesMerch_changeModel_label: changeModelLink, partsAccessoriesMerch_lookingForAlternatives_label: lookingForAlternativesLabel, partsAccessoriesMerch_viewCompatibleItems_label: viewCompatibleItemsLabel, partsAccessoriesMerch_resetModel_link: resetModelLink, partsAccessoriesMerch_addToCartPopUpYouSure_header: youSureHeader, partsAccessoriesMerch_resetModelPopUp_description: resetModelDescription, } = labels;
    const { labels: modalLabels, defaultStartYear, categories, endpoints } = selectModelModalModel;
    const checkCompatibility = useCallback((product) => {
        if (product) {
            setAddedRequiredProducts([]);
            setRequiredProducts([]);
            setSelectedUnit(product.key, suitableProductsKeys, product, dispatch, true);
        }
    }, [dispatch, suitableProductsKeys]);
    useEffect(() => {
        if (cookieCompatibilityProduct && !selectedCompatibilityProduct) {
            getProductsById(`${hyperdriveEndpoints?.productsByIds}${cookieCompatibilityProduct}`)
                .then((products) => {
                if (products?.length) {
                    const product = products[0];
                    setSelectedUnit(product?.key, suitableProductsKeys, product, dispatch);
                }
            })
                .catch(() => {
                dispatch(setSelectedCompatibilityProduct(null));
            });
        }
    }, [
        cookieCompatibilityProduct,
        suitableProductsKeys,
        selectedCompatibilityProduct,
        hyperdriveEndpoints?.productsByIds,
        dispatch,
    ]);
    const openModalHandler = useCallback(() => {
        openSelectModelModal({
            dispatch,
            labels: modalLabels,
            commonLabels,
            endpoints,
            defaultStartYear,
            categories,
            callback: checkCompatibility,
        });
    }, [
        categories,
        checkCompatibility,
        commonLabels,
        defaultStartYear,
        dispatch,
        endpoints,
        modalLabels,
    ]);
    const resetModelHandler = useCallback(() => {
        dispatch(setIsModalShowed({
            type: MODALS.RESET_MODEL_MODAL,
            data: {
                title: youSureHeader,
                description: resetModelDescription,
            },
        }));
    }, [dispatch, resetModelDescription, youSureHeader]);
    const getCheckCompatibilityAlert = (productItem, isCheckAllowed) => {
        if (productItem) {
            const { isSuccessCheck, product } = productItem;
            if (product && isCheckAllowed) {
                const productYear = getProductYear(productItem.product);
                const finallyLabel = productYear ? ` ${productYear} ${product.name} ` : ` ${product.name} `;
                return (React.createElement(FlexWrapper, { mbDesktop: "8px", flexDirection: "column", gap: "24px", isFullWidth: true },
                    React.createElement(CheckPartAlert, { message: React.createElement("span", null,
                            isSuccessCheck
                                ? compatibleWithYourModelAlertDescription
                                : isNotCompatibleWithYourModelAlert,
                            React.createElement(RegularTextLevel4, { color: isSuccessCheck ? colorAccentSuccess800 : colorAccentError900, weight: 500 }, finallyLabel)), type: isSuccessCheck ? AlertType.SUCCESS : AlertType.ERROR, buttonText: changeModelLink, secondaryButtonText: resetModelLink, isRightToLeft: isRightToLeft, primaryClickHandler: openModalHandler, secondaryClickHandler: resetModelHandler }),
                    !isSuccessCheck && (React.createElement(CheckCompatibility, { alertText: lookingForAlternativesLabel, isRightToLeft: isRightToLeft, linkLabel: viewCompatibleItemsLabel, link: plpUrl }))));
            }
        }
        return (isCheckAllowed && (React.createElement(CheckCompatibility, { isRightToLeft: isRightToLeft, onClickHandler: openModalHandler })));
    };
    const removeRequiredProduct = (id) => {
        setAddedRequiredProducts((prevState) => prevState.filter((product) => product.id !== id));
    };
    const addRequiredProduct = (productItem) => {
        setAddedRequiredProducts((prevState) => {
            if (prevState.some((item) => item.id === productItem.id)) {
                return prevState.map((item) => {
                    if (item.id === productItem.id) {
                        return {
                            id: item.id,
                            name: item.name,
                            price: productItem.price,
                            sku: productItem.sku,
                            productUrl: productItem.productUrl,
                        };
                    }
                    return item;
                });
            }
            return [...prevState, productItem];
        });
    };
    useEffect(() => {
        if (selectedCompatibilityProduct &&
            selectedCompatibilityProduct?.isSuccessCheck &&
            requiredItemKeys) {
            const { product } = selectedCompatibilityProduct;
            if (requiredItemKeys?.length && product) {
                const selectedCompatibilityProductAccessories = getAttributeByName(selectedCompatibilityProduct.product.variants[0].attributes, ACCESSORIES_ATTRIBUTE);
                const suitableRequiredItemKeys = getSuitableRequiredItems(requiredItemKeys, selectedCompatibilityProductAccessories);
                const productKeysQueryParam = getKeysQueryParamString(suitableRequiredItemKeys);
                const productsByIdsEndpoint = `${hyperdriveEndpoints?.productsByIds}${productKeysQueryParam}`;
                getProductsById(productsByIdsEndpoint)
                    .then((products) => setRequiredProducts(products))
                    .catch(() => setRequiredProducts((prev) => prev));
            }
        }
        else {
            setRequiredProducts([]);
        }
    }, [selectedCompatibilityProduct, hyperdriveEndpoints?.productsByIds, requiredItemKeys, data]);
    const categoryTitle = useSelector(getCategoryTitle);
    const brandSlug = useSelector(getBrandSlug);
    const pageTitle = useSelector(getTitle);
    const title = composePageTitle({
        productName: data?.name,
        categoryTitle,
        brandSlug,
        pageTitle,
    });
    const productCardHeroImage = useSelector(selectProductCardHeroImage);
    const finalPageTitle = metaTitle || title;
    if (!data) {
        return null;
    }
    return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent },
        React.createElement(React.Fragment, null,
            React.createElement(Helmet, { script: [structuredData] },
                React.createElement("title", null, finalPageTitle),
                React.createElement("meta", { name: "description", content: metaDescription || descriptionProps?.description || '', "data-react-helmet": "true" }),
                React.createElement("meta", { property: "og:title", content: finalPageTitle, "data-react-helmet": "true" }),
                React.createElement("meta", { property: "og:image", content: productCardHeroImage, "data-react-helmet": "true" })),
            React.createElement(ProductDetailsPageWrapperStyled, null,
                React.createElement(Container, null,
                    React.createElement(ProductCardWrapperStyled, { topHorizontalLineColor: currentColorTheme, isRightToLeft: isRightToLeft, id: PRODUCT_CARD_SECTION_ID }, isInEditor || isLoadedProductDetails ? (React.createElement(React.Fragment, null,
                        React.createElement(Row, { mt: mobileMargin, mdMt: tabletMargin, lgMt: desktopMargin, isBrandedCardGrid: true },
                            React.createElement(Col, { lg: 7, md: 8 },
                                !isInfiniteDesktopView && (React.createElement(Description, { ...descriptionProps },
                                    React.createElement(MountingInformationComponent, null))),
                                React.createElement(GalleryContext.Provider, { value: galleryContext },
                                    React.createElement(Gallery, { key: "gallery-accessories", thumbnailAspectRatio: AspectRatio.Square, isZoomed: true, ...galleryComponentProps })),
                                !isEmptyProductBenefits && isInfiniteDesktopView && (React.createElement(ProductBenefits, { ...productBenefitsProps }))),
                            React.createElement(Col, { lg: 5, md: 8 },
                                isInfiniteDesktopView && (React.createElement(Description, { ...descriptionProps },
                                    React.createElement(MountingInformationComponent, null),
                                    getCheckCompatibilityAlert(selectedCompatibilityProduct, isCompatibilityCheckAllowed))),
                                React.createElement(CardPanel, { ...cardPanelComponentProps, addRequiredProduct: addRequiredProduct, addedRequiredProducts: addedRequiredProducts, removeRequiredProduct: removeRequiredProduct, sizeGuideAssetPath: sizeGuideAssetPath, compatibilityAlert: getCheckCompatibilityAlert(selectedCompatibilityProduct, isCompatibilityCheckAllowed), downloadSectionItems: downloadSectionItems, isCompatibilityCheckAllowed: isCompatibilityCheckAllowed, productName: data?.name, warrantyInfo: warrantyInfo, privacyConsentsModel: privacyConsentsModel, fasletModel: fasletModel, requiredProducts: requiredProducts, isRequiredProductExist: Boolean(requiredItemKeys?.length), campaignDisclaimer: campaignDisclaimer, openSelectModelModal: openModalHandler, plpUrl: plpUrl }))),
                        !isEmptyProductBenefits && !isInfiniteDesktopView && (React.createElement(ProductBenefits, { ...productBenefitsProps })))) : (React.createElement(CardSkeleton, { mobileMargin: mobileMargin, tabletMargin: tabletMargin, desktopMargin: desktopMargin }))))),
            (isOpenCartModal || isOpenExternalCartModal) && (React.createElement(CartModalWindow, { productName: data?.name, cartErrorData: cartErrorData, isRightToLeft: isRightToLeft, labels: cartModalLabels, onHideModalCart: onHideModalCart })),
            React.createElement(SpinnerLoader, { isLoading: isAddingProduct || isLoadingExternalCart, fullWidth: true }))));
}
CardAccessories.displayName = 'ProductCardAccessories_AEMComponent';
